import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CreateMerchant.css';
import SearchableSelect from '../../../components/shared/SearchableSelect';
import CreateFlowHero from '../../../components/shared/CreateFlowHero';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { cn } from '../../../lib/utils';
import {
  BsBuilding, BsEnvelopeFill, BsGlobe, BsGlobe2, BsLink45Deg, BsLockFill, BsPhone,
  BsPersonFill, BsShieldFill, BsCheckCircleFill, BsGearFill,
  BsArrowRight, BsArrowLeft, BsCheckLg, BsEye, BsEyeSlash
} from 'react-icons/bs';

import { apiService } from '../../../services/apiService';
import { API_PATHS } from '../../../utils/apiPaths';
import { extractApiData } from '../../../utils/helpers';
import type { CountryOption } from '../../acquirers/models/types';
import {
  MERCHANT_DASHBOARD_KEY_OPTIONS,
  resolveMerchantDashboardKey,
} from '../../../utils/merchantDashboardKey';
import {
  validateEmail,
} from '../../../utils/validation';

const MERCHANT_KEY_MIN = 12;
const MERCHANT_SECRET_MIN = 15;
const MERCHANT_KEY_PATTERN = /^[A-Za-z0-9]{12,}$/;
const MERCHANT_SECRET_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{15,}$/;

function randomFromChars(chars: string, length: number) {
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, (value) => chars[value % chars.length]).join('');
}

function shuffleString(value: string) {
  const chars = value.split('');
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

function generateMerchantKey() {
  return randomFromChars(
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
    16
  );
}

function generateMerchantSecret() {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const specials = '!@#$%^&*()-_=+[]{}';
  const all = `${letters}${digits}${specials}`;
  const seed =
    randomFromChars(letters, 4) +
    randomFromChars(digits, 4) +
    randomFromChars(specials, 3) +
    randomFromChars(all, 8);
  return shuffleString(seed);
}

function validateMerchantKey(value: string) {
  const key = value.trim();
  if (!key) return 'Merchant key is required';
  if (key.length < MERCHANT_KEY_MIN) {
    return `Merchant key must be at least ${MERCHANT_KEY_MIN} characters`;
  }
  if (!MERCHANT_KEY_PATTERN.test(key)) {
    return 'Merchant key must be alphanumeric';
  }
  return '';
}

function validateMerchantSecret(value: string) {
  const secret = value.trim();
  if (!secret) return 'Merchant secret is required';
  if (secret.length < MERCHANT_SECRET_MIN) {
    return `Merchant secret must be at least ${MERCHANT_SECRET_MIN} characters`;
  }
  if (!MERCHANT_SECRET_PATTERN.test(secret)) {
    return 'Merchant secret must include letters, numbers, and special characters';
  }
  return '';
}

function isValidUrl(value?: string) {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateOptionalUrl(value: string, fieldLabel: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!isValidUrl(trimmed)) return `Enter a valid ${fieldLabel} (https://...)`;
  return '';
}

function formatCompanySizeInput(value: string) {
  return value.replace(/[^\d-+]/g, '');
}

function validateCompanySize(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) {
    if (Number(trimmed) <= 0) return 'Company size must be greater than 0';
    return '';
  }
  if (!/^(\d+-\d+|\d+\+)$/.test(trimmed)) {
    return 'Enter a number (e.g., 10) or a range (e.g., 51-200)';
  }
  const rangeMatch = trimmed.match(/^(\d+)-(\d+)$/);
  if (rangeMatch && Number(rangeMatch[1]) >= Number(rangeMatch[2])) {
    return 'Range minimum must be less than maximum (e.g., 51-200)';
  }
  return '';
}

function normalizeUrlForPayload(value: string) {
  return value.trim();
}

function renderReviewUrl(label: string, value?: string) {
  const trimmed = value?.trim();
  return (
    <div key={label} className="cm-review-row">
      <span>{label}</span>
      {trimmed ? (
        <a href={trimmed} target="_blank" rel="noopener noreferrer">{trimmed}</a>
      ) : (
        <strong>—</strong>
      )}
    </div>
  );
}

const STEPS = [
  { id: 1, label: 'Client Info', icon: <BsBuilding /> },
  { id: 2, label: 'Login Credentials', icon: <BsPersonFill /> },
  { id: 3, label: 'API Configuration', icon: <BsGearFill /> },
  { id: 4, label: 'Review & Submit', icon: <BsCheckLg /> },
];

const TIMER_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const seconds = (i + 1) * 5;
  return { value: String(seconds), label: `${seconds} seconds` };
});

// Checkout-page translation — value is the language code sent back as
// CheckoutOrderSummaryResponse.translateLanguage and read by the checkout page's own i18n.
const CHECKOUT_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'tr', label: 'Turkish' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'hi', label: 'Hindi' },
];

const BLANK = {
  clientName: '',
  industry: '',
  websiteUrl: '',
  companySize: '',
  country: '',
  countryId: '',
  city: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  rootAccountName: '',
  loginEmail: '',
  password: '',
  notes: '',
  merchantKey: '',
  merchantSecret: '',
  dashboardKey: 'DEFAULT',
  webhookUrl: '',
  redirectUrl: '',
  whitelistedIps: '',
  redirectTimerSeconds: '5',
  translationEnabled: false,
  translateLanguage: '',
  whiteLabelUrlsEnabled: false,
  latinSupportEnabled: false,
  directMerchantRedirectEnabled: false,
  iframePrefillEnabled: false,
  descriptorDisplayEnabled: false,
  descriptorManualValue: '',
  collectGroupUrlOverrideEnabled: false,
  collectGroupRedirectUrls: '',
};

function mapMerchantApiToForm(data: Record<string, unknown>, merchantId: string) {
  return {
    ...BLANK,
    clientName: String(data.name ?? data.clientName ?? ''),
    industry: String(data.industry ?? data.category ?? ''),
    merchantKey: String(data.merchantKey ?? ''),
    merchantSecret: String(data.merchantSecret ?? ''),
    dashboardKey: resolveMerchantDashboardKey(data),
    websiteUrl: String(data.website ?? data.websiteUrl ?? ''),
    companySize: String(data.companySize ?? ''),
    country: String(data.country ?? ''),
    city: String(data.city ?? ''),
    contactName: String(data.contactName ?? ''),
    contactEmail: String(data.contactEmail ?? ''),
    contactPhone: String(data.contactPhone ?? data.phone ?? ''),
    rootAccountName: String(data.rootAccountName ?? data.contactName ?? ''),
    loginEmail: String(data.loginEmail ?? data.email ?? data.contactEmail ?? ''),
    notes: String(data.notes ?? ''),
    webhookUrl: String(data.webhookUrl ?? ''),
    redirectUrl: String(data.redirectUrl ?? ''),
    whitelistedIps: String(data.whitelistedIps ?? data.whitelistIps ?? ''),
    redirectTimerSeconds: (() => {
      const raw = data.redirectTimerSeconds;
      if (raw == null || raw === '') return '';
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? String(n) : '';
    })(),
    translationEnabled: Boolean(data.translationEnabled),
    whiteLabelUrlsEnabled: Boolean(data.whiteLabelUrlsEnabled),
    latinSupportEnabled: Boolean(data.latinSupportEnabled),
    directMerchantRedirectEnabled: Boolean(data.directMerchantRedirectEnabled),
    iframePrefillEnabled: Boolean(data.iframePrefillEnabled),
    descriptorDisplayEnabled: Boolean(data.descriptorDisplayEnabled),
    descriptorManualValue: data.descriptorManualValue ?? '',
    collectGroupUrlOverrideEnabled: Boolean(data.collectGroupUrlOverrideEnabled),
    collectGroupRedirectUrls: data.collectGroupRedirectUrls ?? '',
    translateLanguage: String(data.translateLanguage ?? ''),
    merchantId: Number(data.id ?? data.merchantId ?? merchantId),
  };
}

function collectStepErrors(
  form: typeof BLANK & { merchantId?: number },
  step: number,
  isEdit = false
): Record<string, string> {
  const e: Record<string, string> = {};

  if (step === 1) {
    if (!form.clientName.trim()) e.clientName = 'Client name is required';
    if (!form.industry.trim()) e.industry = 'Industry is required';
    const websiteError = validateOptionalUrl(form.websiteUrl, 'website URL');
    if (websiteError) e.websiteUrl = websiteError;
    const companySizeError = validateCompanySize(form.companySize);
    if (companySizeError) e.companySize = companySizeError;
    if (!form.countryId) e.country = 'Required';
  }
  if (step === 2) {
    if (!form.contactName.trim()) e.contactName = 'Contact name is required';
    const contactEmailError = validateEmail(form.contactEmail, {
      required: true,
      requiredMessage: 'Contact email is required',
      invalidMessage: 'Invalid email',
    });
    if (contactEmailError) e.contactEmail = contactEmailError;
  }
  if (step === 3) {
    if (isEdit) {
      // Blank means "keep the current key/secret" on edit — only validate format when changed.
      if (form.merchantKey.trim()) {
        const merchantKeyError = validateMerchantKey(form.merchantKey);
        if (merchantKeyError) e.merchantKey = merchantKeyError;
      }
      if (form.merchantSecret.trim()) {
        const merchantSecretError = validateMerchantSecret(form.merchantSecret);
        if (merchantSecretError) e.merchantSecret = merchantSecretError;
      }
    } else {
      const merchantKeyError = validateMerchantKey(form.merchantKey);
      if (merchantKeyError) e.merchantKey = merchantKeyError;
      const merchantSecretError = validateMerchantSecret(form.merchantSecret);
      if (merchantSecretError) e.merchantSecret = merchantSecretError;
    }
    const urlFields: Array<['webhookUrl' | 'redirectUrl', string]> = [
      ['webhookUrl', 'webhook URL'],
      ['redirectUrl', 'redirect URL'],
    ];
    for (const [field, label] of urlFields) {
      const urlError = validateOptionalUrl(form[field], label);
      if (urlError) e[field] = urlError;
    }
  }

  return e;
}

function resolveLoginFields(values: typeof BLANK) {
  const contactName = values.contactName.trim();
  const contactEmail = values.contactEmail.trim();
  return {
    rootAccountName: values.rootAccountName.trim() || contactName,
    loginEmail: values.loginEmail.trim() || contactEmail,
  };
}

const CreateMerchant: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<any>({ ...BLANK });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showMerchantSecret, setShowMerchantSecret] = useState(false);
  const [countries, setCountries] = useState<CountryOption[]>([]);

  const countrySelectOptions = useMemo(
    () =>
      [...countries]
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((c) => ({
          value: String(c.autoId),
          label: c.countryName,
          searchText: c.countryFlag,
        })),
    [countries],
  );

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const countriesRes: any = await apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES);

        const countriesRaw = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes)
            ? countriesRes
            : [];
        const countryList = countriesRaw
          .map((c: any) => ({
            autoId: Number(c.autoId ?? c.id ?? 0),
            countryName: String(c.countryName ?? c.name ?? '').trim(),
            countryFlag: String(c.countryFlag ?? '').trim().toUpperCase(),
          }))
          .filter(
            (c: CountryOption) =>
              c.autoId > 0 && c.countryName.length > 0 && c.countryFlag.length === 2,
          );
        setCountries(countryList);
      } catch (err) {
        console.error('Failed to fetch dropdown options', err);
      }
    };
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    if (!form.country || countries.length === 0 || form.countryId) return;
    const match = countries.find(
      (c) => c.countryName.toLowerCase() === form.country.trim().toLowerCase(),
    );
    if (match) {
      setForm((prev: typeof BLANK) => ({ ...prev, countryId: String(match.autoId) }));
    }
  }, [countries, form.country, form.countryId]);

  useEffect(() => {
    if (id) fetchMerchant(id);
  }, [id]);

  const fetchMerchant = async (merchantId: string) => {
    setFetchingData(true);
    setApiError(null);
    try {
      const res: any = await apiService.get(`${API_PATHS.GET_MERCHANT}/${merchantId}`);
      const data = extractApiData<Record<string, unknown>>(res);
      if (data && res.success !== false) {
        setForm(mapMerchantApiToForm(data, merchantId));
      } else {
        setApiError('Merchant not found');
      }
    } catch (err: any) {
      console.error('Fetch Merchant Error:', err);
      setApiError('Failed to load merchant data');
    } finally {
      setFetchingData(false);
    }
  };

  const set = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined } as any));
    }
  };

  const validate = (s: number): boolean => {
    const e = collectStepErrors(form, s, isEdit);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getFirstInvalidStep = (): number | null => {
    for (const s of [1, 2, 3]) {
      const e = collectStepErrors(form, s, isEdit);
      if (Object.keys(e).length > 0) {
        setErrors(e);
        return s;
      }
    }
    setErrors({});
    return null;
  };

  const next = () => {
    if (step === 2) {
      const loginFields = resolveLoginFields(form);
      const syncedForm = { ...form, ...loginFields };
      setForm(syncedForm);
      const e = collectStepErrors(syncedForm, step, isEdit);
      setErrors(e);
      if (Object.keys(e).length === 0) setStep((s) => s + 1);
      return;
    }
    if (validate(step)) setStep((s) => s + 1);
  };
  const back = () => { setErrors({}); setStep(s => s - 1); };

  const buildPayload = (values: any) => {
    const loginFields = resolveLoginFields(values);
    return {
      name: values.clientName,
      industry: values.industry,
      merchantKey: values.merchantKey?.trim() || undefined,
      merchantSecret: values.merchantSecret?.trim() || undefined,
      dashboardKey: resolveMerchantDashboardKey(values.dashboardKey),
      website: normalizeUrlForPayload(values.websiteUrl) || undefined,
      companySize: values.companySize?.trim() || undefined,
      country: values.country,
      city: values.city,
      contactName: values.contactName,
      contactEmail: values.contactEmail,
      contactPhone: values.contactPhone?.trim() || undefined,
      rootAccountName: loginFields.rootAccountName,
      loginEmail: loginFields.loginEmail,
      email: loginFields.loginEmail,
      password: values.password?.trim() || undefined,
      notes: values.notes,
      webhookUrl: normalizeUrlForPayload(values.webhookUrl) || undefined,
      redirectUrl: normalizeUrlForPayload(values.redirectUrl) || undefined,
      whitelistedIps: values.whitelistedIps,
      redirectTimerSeconds: values.redirectTimerSeconds
        ? Number(values.redirectTimerSeconds)
        : undefined,
      translationEnabled: values.translationEnabled,
      translateLanguage: values.translationEnabled ? (values.translateLanguage || undefined) : undefined,
      whiteLabelUrlsEnabled: values.whiteLabelUrlsEnabled,
      latinSupportEnabled: values.latinSupportEnabled,
      directMerchantRedirectEnabled: values.directMerchantRedirectEnabled,
      iframePrefillEnabled: values.iframePrefillEnabled,
      descriptorDisplayEnabled: values.descriptorDisplayEnabled,
      // Empty string clears it server-side, which falls the name back to the storefront URL.
      descriptorManualValue: values.descriptorManualValue.trim(),
      collectGroupUrlOverrideEnabled: values.collectGroupUrlOverrideEnabled,
      // Empty clears it server-side; only meaningful when the toggle is on.
      collectGroupRedirectUrls: values.collectGroupUrlOverrideEnabled
        ? values.collectGroupRedirectUrls.trim() : '',
    };
  };

  const submitMerchant = async () => {
    const invalidStep = getFirstInvalidStep();
    if (invalidStep) {
      setStep(invalidStep);
      return;
    }

    setLoading(true);
    setApiError(null);
    try {
      const payload = buildPayload(form);
      if (isEdit) {
        if (!form.merchantId) {
          setApiError('Merchant ID is missing. Please reload the merchant and try again.');
          setLoading(false);
          return;
        }
        await apiService.post(API_PATHS.UPDATE_MERCHANT, {
          ...payload,
          merchantId: Number(form.merchantId),
        });
      } else {
        await apiService.post(API_PATHS.ADD_MERCHANT, payload);
      }
      setDone(true);
    } catch (err: any) {
      console.error('Submit Merchant Error:', err);
      setApiError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} merchant. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 4) {
      next();
      return;
    }
    submitMerchant();
  };

  const handleReset = () => {
    setForm({ ...BLANK });
    setStep(1);
    setDone(false);
    setErrors({});
    if (isEdit) navigate('/dashboard/merchants/create');
  };

  if (done) {
    return (
      <div className="cm-page">
        <div className="cm-success-wrap">
          <div className="cm-success-icon"><BsCheckCircleFill /></div>
          <h2 className="cm-success-title">Merchant {isEdit ? 'Updated' : 'Created'}!</h2>
          <p className="cm-success-sub">
            <strong>{form.clientName}</strong> has been successfully {isEdit ? 'updated' : 'onboarded'}.
          </p>
          <div className="cm-success-details">
            <div className="cm-sdet"><span>Login Email</span><strong>{form.loginEmail}</strong></div>
            <div className="cm-sdet"><span>Country</span><strong>{form.country || '—'}</strong></div>
            <div className="cm-sdet"><span>Industry</span><strong>{form.industry || '—'}</strong></div>
          </div>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard/merchants/manage')}>
              Back to List
            </Button>
            {!isEdit && <Button onClick={handleReset}>Create Another</Button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-page dh-page">
      <CreateFlowHero
        badge={isEdit ? 'Edit Merchant' : 'New Merchant'}
        title={isEdit ? 'Edit Merchant' : 'Create Merchant'}
        subtitle={
          isEdit
            ? `Modifying profile for ${form.clientName || 'merchant'}`
            : 'Fill in the steps below to onboard a new merchant'
        }
        icon={<BsBuilding />}
        onBack={() => navigate('/dashboard/merchants/manage')}
        backLabel="Back to List"
      />

      <div className="cm-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`cm-step ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`}>
              <div className="cm-step-circle">
                {step > s.id ? <BsCheckLg /> : s.icon}
              </div>
              <span className="cm-step-label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`cm-step-line ${step > s.id ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="cm-card" style={{ position: 'relative' }}>
        {fetchingData && (
          <div className="cm-loading-overlay">
            <div className="ln-spinner" />
          </div>
        )}
        <form onSubmit={handleSubmit}>

          {/* Step 1 — Client Info */}
          {step === 1 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title"><BsBuilding /> Company Information</h3>
              <div className="cm-grid cm-grid-4">
                <label className="cm-label">
                  <span>Company Name <em>*</em></span>
                  <Input
                    className={cn(errors.clientName && 'border-destructive')}
                    value={form.clientName}
                    onChange={e => set('clientName', e.target.value)}
                    placeholder="Enter company name"
                  />
                  {errors.clientName && <p className="cm-err">{errors.clientName}</p>}
                </label>
                <label className="cm-label">
                  <span>Industry <em>*</em></span>
                  <Input
                    className={cn(errors.industry && 'border-destructive')}
                    value={form.industry}
                    onChange={e => set('industry', e.target.value)}
                    placeholder="e.g., Forex"
                  />
                  {errors.industry && <p className="cm-err">{errors.industry}</p>}
                </label>
                <label className="cm-label">
                  <span>Website URL</span>
                  <div className="relative">
                    <BsGlobe className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="url"
                      className={cn('pl-9', errors.websiteUrl && 'border-destructive')}
                      value={form.websiteUrl}
                      onChange={e => set('websiteUrl', e.target.value)}
                      placeholder="https://www.example.com"
                    />
                  </div>
                  {errors.websiteUrl && <p className="cm-err">{errors.websiteUrl}</p>}
                </label>
                <label className="cm-label">
                  <span>Company Size</span>
                  <Input
                    className={cn(errors.companySize && 'border-destructive')}
                    value={form.companySize}
                    onChange={e => set('companySize', formatCompanySizeInput(e.target.value))}
                    placeholder="e.g., 10 or 51-200"
                  />
                  {errors.companySize && <p className="cm-err">{errors.companySize}</p>}
                </label>
                <label className="cm-label">
                  <span>Country <em>*</em></span>
                  <div className="relative">
                    <BsGlobe2 className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                    <SearchableSelect
                      className="h-9 pl-9"
                      value={form.countryId}
                      options={countrySelectOptions}
                      onChange={(countryId) => {
                        const country = countries.find((c) => String(c.autoId) === countryId);
                        set('countryId', countryId);
                        set('country', country?.countryName ?? '');
                      }}
                      placeholder="Select country"
                      searchPlaceholder="Search country…"
                      emptyMessage="No countries available."
                      error={!!errors.country}
                      aria-label="Country"
                    />
                  </div>
                  {errors.country && <p className="cm-err">{errors.country}</p>}
                </label>
                <label className="cm-label">
                  <span>City</span>
                  <Input
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    placeholder="e.g., Lagos"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Step 2 — Login Credentials */}
          {step === 2 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title"><BsPersonFill /> Login Credentials</h3>
              <div className="cm-section-block">
                {/* <div className="cm-section-head">
                  <h4 className="cm-section-title">Login Credentials</h4>
                  <span className="cm-section-hint">Main point of contact for this client</span>
                </div> */}
                <div className="cm-grid cm-grid-3">
                  <label className="cm-label">
                    <span>Name <em>*</em></span>
                    <Input
                      className={cn(errors.contactName && 'border-destructive')}
                      value={form.contactName}
                      onChange={e => set('contactName', e.target.value)}
                      placeholder="Enter your name (e.g., John Doe)"
                    />
                    {errors.contactName && <p className="cm-err">{errors.contactName}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Email <em>*</em></span>
                    <div className="relative">
                      <BsEnvelopeFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        className={cn('pl-9', errors.contactEmail && 'border-destructive')}
                        value={form.contactEmail}
                        onChange={e => set('contactEmail', e.target.value)}
                        placeholder="contact@example.com"
                        type="email"
                      />
                    </div>
                    {errors.contactEmail && <p className="cm-err">{errors.contactEmail}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Phone</span>
                    <div className="relative">
                      <BsPhone className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="tel"
                        className={cn('pl-9', errors.contactPhone && 'border-destructive')}
                        value={form.contactPhone}
                        onChange={e => set('contactPhone', e.target.value)}
                        placeholder="8012345678"
                      />
                    </div>
                    {errors.contactPhone && <p className="cm-err">{errors.contactPhone}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Password</span>
                    <div className="relative">
                      <Input
                        className="pr-10"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={e => set('password', e.target.value)}
                        placeholder="Enter secure password"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <BsEyeSlash /> : <BsEye />}
                      </Button>
                    </div>
                  </label>
                </div>
              </div>

              {/* <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">Login Credentials</h4>
                  <span className="cm-section-hint">Portal access for client user</span>
                </div>
                <div className="cm-grid cm-grid-3">
                  <label className="cm-label">
                    <span>Root Account Name <em>*</em></span>
                    <div className="cm-input-icon">
                      <BsPersonFill />
                      <input
                        className={`cm-input has-icon ${errors.rootAccountName ? 'err' : ''}`}
                        value={form.rootAccountName}
                        onChange={e => set('rootAccountName', e.target.value)}
                        placeholder="Enter root user name"
                      />
                    </div>
                    {errors.rootAccountName && <p className="cm-err">{errors.rootAccountName}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Login Email <em>*</em></span>
                    <div className="cm-input-icon">
                      <BsEnvelopeFill />
                      <input
                        className={`cm-input has-icon ${errors.loginEmail ? 'err' : ''}`}
                        value={form.loginEmail}
                        onChange={e => set('loginEmail', e.target.value)}
                        placeholder="user@example.com"
                        type="email"
                      />
                    </div>
                    {errors.loginEmail && <p className="cm-err">{errors.loginEmail}</p>}
                  </label>
                  
                </div>
              </div> */}

              <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">Notes</h4>
                  {/* <span className="cm-section-hint cm-section-hint-italic">KYC, payout preferences, support notes</span> */}
                </div>
                <label className="cm-label">
                  <Textarea
                    value={form.notes}
                    onChange={e => set('notes', e.target.value)}
                    placeholder="KYC status, payout prefs, support notes..."
                    rows={4}
                  />
                </label>
              </div>
            </div>
          )}

          {/* Step 3 — API Configuration */}
          {step === 3 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title"><BsGearFill /> API Configuration</h3>
              <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">API Credentials</h4>
                  <span className="cm-section-hint">Merchant key and secret for API access</span>
                </div>
                <div className="cm-grid">
                  <label className="cm-label cm-span2">
                    <span>Merchant Key {!isEdit && <em>*</em>}</span>
                    <div className="cm-input-action-row">
                      <Input
                        className={cn('flex-1', errors.merchantKey && 'border-destructive')}
                        value={form.merchantKey}
                        onChange={e => set('merchantKey', e.target.value)}
                        placeholder="Minimum 12 alphanumeric characters"
                        autoComplete="off"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => set('merchantKey', generateMerchantKey())}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="cm-field-hint">
                      At least 12 alphanumeric characters. Used as the merchant API key.
                      {isEdit && ' Leave unchanged to keep the current key.'}
                    </p>
                    {errors.merchantKey && <p className="cm-err">{errors.merchantKey}</p>}
                  </label>
                  <label className="cm-label cm-span2">
                    <span>Merchant Secret {!isEdit && <em>*</em>}</span>
                    <div className="cm-input-action-row">
                      <div className="relative flex-1">
                        <BsLockFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className={cn('pl-9 pr-10', errors.merchantSecret && 'border-destructive')}
                          type={showMerchantSecret ? 'text' : 'password'}
                          value={form.merchantSecret}
                          onChange={e => set('merchantSecret', e.target.value)}
                          placeholder="Minimum 15 characters with letters, numbers, and symbols"
                          autoComplete="new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowMerchantSecret(v => !v)}
                          aria-label={showMerchantSecret ? 'Hide merchant secret' : 'Show merchant secret'}
                        >
                          {showMerchantSecret ? <BsEyeSlash /> : <BsEye />}
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => set('merchantSecret', generateMerchantSecret())}
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="cm-field-hint">
                      At least 15 characters including letters, numbers, and special characters.
                      {isEdit && ' Leave unchanged to keep the current secret.'}
                    </p>
                    {errors.merchantSecret && <p className="cm-err">{errors.merchantSecret}</p>}
                  </label>
                  <label className="cm-label cm-span2">
                    <span>Dashboard</span>
                    <SearchableSelect
                      className="h-9"
                      value={form.dashboardKey}
                      options={[...MERCHANT_DASHBOARD_KEY_OPTIONS]}
                      onChange={(value) => set('dashboardKey', resolveMerchantDashboardKey(value))}
                      placeholder="Select dashboard"
                      searchPlaceholder="Search dashboards…"
                      emptyMessage="No dashboard options available."
                      aria-label="Merchant dashboard"
                    />
                    <p className="cm-field-hint">
                      Default keeps the previous merchant dashboard. The other option uses the Salesforce console.
                    </p>
                  </label>
                </div>
              </div>
              <div className="cm-section-block">
                <div className="cm-section-head">
                  <h4 className="cm-section-title">API Configuration</h4>
                  <span className="cm-section-hint">Callback and redirect endpoints</span>
                </div>
                <div className="cm-grid">
                  <label className="cm-label">
                    <span>Webhook URL</span>
                    <div className="relative">
                      <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="url"
                        className={cn('pl-9', errors.webhookUrl && 'border-destructive')}
                        value={form.webhookUrl}
                        onChange={e => set('webhookUrl', e.target.value)}
                        placeholder="https://your-domain.com/webhook"
                      />
                    </div>
                    {errors.webhookUrl && <p className="cm-err">{errors.webhookUrl}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Redirect URL</span>
                    <div className="relative">
                      <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="url"
                        className={cn('pl-9', errors.redirectUrl && 'border-destructive')}
                        value={form.redirectUrl}
                        onChange={e => set('redirectUrl', e.target.value)}
                        placeholder="https://client.com/pay"
                      />
                    </div>
                    {errors.redirectUrl && <p className="cm-err">{errors.redirectUrl}</p>}
                  </label>
                  <label className="cm-label">
                    <span>Redirect Timer (seconds)</span>
                    <SearchableSelect
                      className="h-9"
                      value={form.redirectTimerSeconds}
                      options={TIMER_OPTIONS}
                      onChange={(value) => set('redirectTimerSeconds', value)}
                      placeholder="Select timer"
                      searchPlaceholder="Search seconds…"
                      emptyMessage="No timer options available."
                      aria-label="Redirect timer in seconds"
                    />
                    <p className="cm-field-hint">Redirect timer up to 60 seconds, in 5-second increments.</p>
                  </label>
                  <label className="cm-label cm-span2">
                    <span>Whitelisted IPs</span>
                    <Textarea
                      value={form.whitelistedIps}
                      onChange={e => set('whitelistedIps', e.target.value)}
                      placeholder="One IP per line or separate with commas"
                      rows={4}
                    />
                    <p className="cm-field-hint">Example: 103.21.244.0/24, 13.40.254.240</p>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Enable checkout translation</span>
                        <p className="cm-field-hint">
                          When on, this merchant's hosted checkout page renders in the selected
                          language instead of English.
                        </p>
                      </div>
                      <Switch
                        checked={form.translationEnabled}
                        onCheckedChange={(checked) => set('translationEnabled', checked)}
                        aria-label="Enable checkout translation"
                      />
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Enable white-label URLs</span>
                        <p className="cm-field-hint">
                          When on, every link this merchant receives from the payment API — and every
                          page their customers land on — uses the BEMI domains instead of MoneyEU's.
                          Leave off to keep MoneyEU URLs.
                        </p>
                      </div>
                      <Switch
                        checked={form.whiteLabelUrlsEnabled}
                        onCheckedChange={(checked) => set('whiteLabelUrlsEnabled', checked)}
                        aria-label="Enable white-label URLs"
                      />
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Enable Latin support</span>
                        <p className="cm-field-hint">
                          When on, the hosted checkout shows extra "Latin characters" name &amp; address
                          fields. The shopper's local-language name/address is kept on the order for
                          record, and the Latin values are what get forwarded to the acquirer.
                        </p>
                      </div>
                      <Switch
                        checked={form.latinSupportEnabled}
                        onCheckedChange={(checked) => set('latinSupportEnabled', checked)}
                        aria-label="Enable Latin support"
                      />
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Enable direct merchant redirection</span>
                        <p className="cm-field-hint">
                          When on, the shopper skips our hosted status page and is sent straight to the
                          merchant's own return URL after payment — the URL sent in the processPayment
                          request if present, otherwise the merchant's configured redirect URL. Leave
                          off to show our status page first.
                        </p>
                      </div>
                      <Switch
                        checked={form.directMerchantRedirectEnabled}
                        onCheckedChange={(checked) => set('directMerchantRedirectEnabled', checked)}
                        aria-label="Enable direct merchant redirection"
                      />
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Enable iframe prefill</span>
                        <p className="cm-field-hint">
                          When on, card details this merchant submits over S2S are not sent straight to
                          the acquirer. Instead we hold the card, create the transaction, and serve our
                          own hosted card iframe (styled like the Axipays form, with the trading name on
                          top) prefilled with it; on Pay the card is routed to whatever MID the routing
                          assigns. Shopper experience and the masked network URLs stay the same.
                        </p>
                      </div>
                      <Switch
                        checked={form.iframePrefillEnabled}
                        onCheckedChange={(checked) => set('iframePrefillEnabled', checked)}
                        aria-label="Enable iframe prefill"
                      />
                    </div>
                  </label>
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Show trading name on checkout</span>
                        <p className="cm-field-hint">
                          When on, the merchant's trading name appears at the top of the hosted
                          checkout, and above the card form for Axipays iframe terminals. It is a
                          display name only &mdash; it does not change the billing descriptor the
                          cardholder sees on their statement, which comes from the acquirer. Keep the
                          two consistent, or shoppers won't recognise the charge. Pure S2S payments
                          have no checkout page of ours, so nothing shows there.
                        </p>
                      </div>
                      <Switch
                        checked={form.descriptorDisplayEnabled}
                        onCheckedChange={(checked) => set('descriptorDisplayEnabled', checked)}
                        aria-label="Show trading name on checkout"
                      />
                    </div>
                  </label>
                  {form.descriptorDisplayEnabled && (
                    <label className="cm-label cm-span2">
                      <span>Trading name (optional)</span>
                      <Input
                        value={form.descriptorManualValue}
                        maxLength={64}
                        onChange={(e) => set('descriptorManualValue', e.target.value)}
                        placeholder="Leave empty to use the storefront URL"
                      />
                      <p className="cm-field-hint">
                        Enter a name and it is shown exactly as typed. Leave it empty and the name
                        comes from the order's storefront URL &mdash; "americanpeptides.us" shows as
                        "americanpeptides". Match the billing descriptor on the cardholder's
                        statement, or shoppers won't recognise the charge.
                      </p>
                    </label>
                  )}
                  <label className="cm-label cm-span2">
                    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                      <div className="space-y-0">
                        <span>Collect Group URL override</span>
                        <p className="cm-field-hint">
                          Collect Group (CollectNExchange) only. When on, the return URLs sent to
                          Collect Group are built from one of the approved URLs below instead of the
                          default host &mdash; so traffic reaches Collect Group on a domain they have
                          approved for this merchant. Those URLs must point at our frontend (the
                          shopper returns to the status page there). No effect on any other acquirer.
                        </p>
                      </div>
                      <Switch
                        checked={form.collectGroupUrlOverrideEnabled}
                        onCheckedChange={(checked) => set('collectGroupUrlOverrideEnabled', checked)}
                        aria-label="Collect Group URL override"
                      />
                    </div>
                  </label>
                  {form.collectGroupUrlOverrideEnabled && (
                    <label className="cm-label cm-span2">
                      <span>Approved Collect Group URLs</span>
                      <Input
                        value={form.collectGroupRedirectUrls}
                        onChange={(e) => set('collectGroupRedirectUrls', e.target.value)}
                        placeholder="https://approved1.com, https://approved2.com"
                      />
                      <p className="cm-field-hint">
                        Comma-separated. One is picked per transaction (stable per transaction, spread
                        across the list). Each must be a full base URL (https://&hellip;) that serves
                        our checkout frontend.
                      </p>
                    </label>
                  )}
                  {form.translationEnabled && (
                    <label className="cm-label">
                      <span>Checkout language</span>
                      <SearchableSelect
                        className="h-9"
                        value={form.translateLanguage}
                        options={CHECKOUT_LANGUAGE_OPTIONS}
                        onChange={(value) => set('translateLanguage', value)}
                        placeholder="Select language"
                        searchPlaceholder="Search language…"
                        emptyMessage="No languages available."
                        aria-label="Checkout language"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Review */}
          {step === 4 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title"><BsShieldFill /> Review &amp; Submit</h3>
              <div className="cm-review-grid">
                <div className="cm-review-section">
                  <div className="cm-review-section-title"><BsBuilding /> Client Info</div>
                  {[
                    ['Client Name', form.clientName || '—'],
                    ['Industry', form.industry || '—'],
                    ['Company Size', form.companySize || '—'],
                    ['Country', form.country || '—'],
                    ['City', form.city || '—'],
                  ].map(([k, v]) => (
                    <div key={k} className="cm-review-row"><span>{k}</span><strong>{v}</strong></div>
                  ))}
                  {renderReviewUrl('Website', form.websiteUrl)}
                </div>
                <div className="cm-review-section">
                  <div className="cm-review-section-title"><BsPersonFill /> Contact &amp; Login</div>
                  {[
                    ['Contact Name', form.contactName || '—'],
                    ['Contact Email', form.contactEmail || '—'],
                    ['Contact Phone', form.contactPhone || '—'],
                    ['Root Account Name', form.rootAccountName || '—'],
                    ['Login Email', form.loginEmail || '—'],
                    ['Password', form.password ? '••••••••' : '—'],
                    ['Notes', form.notes || '—'],
                  ].map(([k, v]) => (
                    <div key={k} className="cm-review-row"><span>{k}</span><strong>{v}</strong></div>
                  ))}
                  <div className="cm-review-section-title" style={{ marginTop: '1.25rem' }}><BsGearFill /> API Configuration</div>
                  {[
                    ...(!isEdit
                      ? [
                        ['Merchant Key', form.merchantKey ? `${form.merchantKey.slice(0, 4)}••••${form.merchantKey.slice(-4)}` : '—'],
                        ['Merchant Secret', form.merchantSecret ? '••••••••••••' : '—'],
                      ]
                      : []),
                    ['Redirect Timer', form.redirectTimerSeconds ? `${form.redirectTimerSeconds} seconds` : '—'],
                    ['Dashboard', MERCHANT_DASHBOARD_KEY_OPTIONS.find((option) => option.value === form.dashboardKey)?.label || 'Default'],
                    ['Whitelisted IPs', form.whitelistedIps || '—'],
                    ['White-label URLs', form.whiteLabelUrlsEnabled ? 'On (BEMI domains)' : 'Off (MoneyEU domains)'],
                    ['Latin Support', form.latinSupportEnabled ? 'On' : 'Off'],
                    ['Direct Merchant Redirect', form.directMerchantRedirectEnabled ? 'On' : 'Off'],
                    [
                      'Checkout trading name',
                      form.descriptorDisplayEnabled
                        ? form.descriptorManualValue.trim() || 'From storefront URL'
                        : 'Off',
                    ],
                    ['Checkout Translation', form.translationEnabled
                      ? (CHECKOUT_LANGUAGE_OPTIONS.find((o) => o.value === form.translateLanguage)?.label || 'Enabled (no language selected)')
                      : 'Off (English)'],
                  ].map(([k, v]) => (
                    <div key={k} className="cm-review-row"><span>{k}</span><strong>{v}</strong></div>
                  ))}
                  {renderReviewUrl('Webhook URL', form.webhookUrl)}
                  {renderReviewUrl('Redirect URL', form.redirectUrl)}
                </div>
              </div>
            </div>
          )}

          <div className="cm-nav">
            {step === 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard/merchants/manage')}
              >
                Cancel
              </Button>
            )}
            {step > 1 && (
              <Button type="button" variant="outline" onClick={back}>
                <BsArrowLeft /> Back
              </Button>
            )}
            <div className="flex-1" />
            {step < 4 ? (
              <Button type="button" onClick={next}>
                {step === 3 ? <>Review <BsArrowRight /></> : <>Continue <BsArrowRight /></>}
              </Button>
            ) : (
              <Button type="button" disabled={loading} onClick={submitMerchant}>
                {loading ? 'Processing...' : <><BsCheckCircleFill /> {isEdit ? 'Update Merchant' : 'Create Merchant'}</>}
              </Button>
            )}
          </div>

          {apiError && (
            <div className="cm-api-err">
              {apiError}
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default CreateMerchant;
